import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, BookOpen, ShieldCheck, Sparkle } from "@phosphor-icons/react";

const AUTH_BG =
  "https://images.unsplash.com/photo-1760307837453-ce60cb209e52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwbGVkZ2VyJTIwYm9va3xlbnwwfHx8fDE3ODQ5NDI4MzR8MA&ixlib=rb-4.1.0&q=85";

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.15fr_0.85fr]" style={{ background: "var(--bk-page)" }}>
      <div className="hidden lg:block relative">
        <img src={AUTH_BG} alt="Ledger book" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(240,230,211,0.08), rgba(26,39,68,0.82))" }} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(232,168,56,0.24),transparent_28%)]" />
        <div className="absolute inset-x-0 bottom-0 p-12">
          <Link to="/" className="flex items-center gap-2.5 mb-6">
            <img src="/ironroot-logo.png" alt="IronRoot" className="w-12 h-12 object-contain" />
            <span className="font-display text-xl font-bold text-white">IronRoot</span>
          </Link>
          <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6" style={{ background: "rgba(26,39,68,0.75)", color: "#F0E6D3", border: "1px solid rgba(240,230,211,0.22)" }}>
            <Sparkle size={14} weight="fill" style={{ color: "var(--bk-gold)" }} /> Rowan keeps things clear, calm, and moving.
          </span>
          <p className="font-display text-4xl font-bold text-white max-w-lg leading-tight">
            Financial Support From The Start.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-8 max-w-lg">
            {[
              { icon: ShieldCheck, title: "Safe by design", desc: "Secure bookkeeping with structure you can trust." },
              { icon: ArrowRight, title: "Approachable", desc: "A modern fintech feel with Rowan’s friendly voice." },
            ].map((item) => (
              <div key={item.title} className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.12)" }}>
                <item.icon size={18} weight="duotone" color="#fff" />
                <p className="text-sm font-semibold text-white mt-3">{item.title}</p>
                <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.72)" }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12" style={{ background: "var(--bk-page)" }}>
        <div className="w-full max-w-md bk-fade-up bk-card p-8 sm:p-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }}>
            <BookOpen size={14} weight="duotone" /> IronRoot access
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2" style={{ color: "var(--bk-text)" }}>{title}</h1>
          <p className="text-base mb-8 leading-relaxed" style={{ color: "var(--bk-text-2)" }}>{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full px-4 py-3 rounded-xl border bg-white text-sm outline-none focus:ring-2 transition-shadow";
const inputStyle = { borderColor: "var(--bk-border)", color: "var(--bk-text)" };

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [invitePreview, setInvitePreview] = useState(null);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const { loginWith } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const teamInviteToken = params.get("team_invite") || "";

  useEffect(() => {
    if (!teamInviteToken) return;
    api.get(`/team-invites/preview?token=${teamInviteToken}`)
      .then((res) => {
        setInvitePreview(res.data);
        if (res.data?.email) setEmail(res.data.email);
      })
      .catch(() => setInvitePreview({ status: "expired" }));
  }, [teamInviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, team_invite_token: teamInviteToken });
      loginWith(data.token, data.user);
      if (data.accepted_team_invite?.accepted) {
        toast.success("Team invite accepted.", { description: data.accepted_team_invite.rowan_message || `You now have access to ${data.accepted_team_invite.business_name}.` });
      } else if (data.team_invite_error) {
        toast.error(data.team_invite_error);
      }
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async () => {
    const target = (forgotEmail || email).trim();
    if (!target || !target.includes("@")) return toast.error("Enter your account email first.");
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: target });
      toast.success("If an account exists for that email, a reset link is on its way.");
      setShowForgot(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to continue your bookkeeping progress.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {params.get("reset") === "success" && (
          <div data-testid="reset-success-banner" className="text-sm px-4 py-3 rounded-xl" style={{ background: "var(--bk-success-bg, #E8F5EC)", color: "var(--bk-success, #1E7B3C)" }}>
            Password updated. You can now log in.
          </div>
        )}
        {teamInviteToken && (
          <div data-testid="login-team-invite-banner" className="text-sm px-4 py-3 rounded-2xl" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)", border: "1px solid rgba(232,168,56,0.28)" }}>
            {invitePreview?.status === "expired"
              ? "That team invite looks expired or unavailable. You can still log in, but we may need to resend the invite."
              : `You've been invited to ${invitePreview?.business_name || "an IronRoot business"} as ${invitePreview?.role_label || "a team member"}. Log in with ${invitePreview?.email || "the invited email"} to accept it.`}
          </div>
        )}
        {error && (
          <div data-testid="login-error" className="text-sm px-4 py-3 rounded-xl" style={{ background: "#FDECE8", color: "#B23B1F" }}>
            {error}
          </div>
        )}
        <div>
          <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Email</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls + " mt-1.5"} style={inputStyle} placeholder="you@company.com" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Password</label>
          <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls + " mt-1.5"} style={inputStyle} placeholder="••••••••" />
          <div className="text-right mt-1.5">
            <button type="button" data-testid="forgot-password-link" onClick={() => setShowForgot((s) => !s)} className="text-xs font-semibold hover:underline" style={{ color: "var(--bk-accent)" }}>Forgot password?</button>
          </div>
          {showForgot && (
            <div data-testid="forgot-password-form" className="mt-2 p-3 rounded-xl border" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }}>
              <p className="text-xs mb-2" style={{ color: "var(--bk-text-2)" }}>Enter your account email and we'll send you a reset link.</p>
              <div className="flex gap-2">
                <input data-testid="forgot-email-input" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border bg-white text-sm outline-none focus:ring-2" style={inputStyle} placeholder="you@company.com" />
                <button type="button" data-testid="send-reset-link-btn" onClick={sendReset} disabled={forgotLoading} className="bk-btn-primary px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap disabled:opacity-60">
                  {forgotLoading ? "Sending…" : "Send reset link"}
                </button>
              </div>
            </div>
          )}
        </div>
        <button data-testid="login-submit" type="submit" disabled={loading} className="bk-btn-primary w-full py-3 text-sm font-semibold disabled:opacity-60">
          {loading ? "Logging in…" : "Log in"}
        </button>
      </form>
      <p className="text-sm mt-6" style={{ color: "var(--bk-text-2)" }}>
        No account?{" "}
        <Link to={teamInviteToken ? `/register?team_invite=${teamInviteToken}` : "/register"} className="font-semibold" style={{ color: "var(--bk-accent)" }} data-testid="link-register">Create one</Link>
      </p>
    </AuthShell>
  );
}

export { inputCls, inputStyle };
